// IssueCard — one human issue from C's retention queue, as a person would say it:
// what they said (the quote), where it came from, where it stands, and the ONE
// thing to do about it. Never a key=value string. Shared by the customer window's
// AI insights tab, its at-risk banner and the dashboard queue.
import React from "react";
import { Phone, MessageSquare, ClipboardList, ArrowUpRight } from "lucide-react";
import Badge from "../../ui/badge";
import Button from "../../ui/button";
import { issueTone, daysOpen } from "../../../api/retentionApi";
import "./atRisk.css";

const SOURCE_WORD = { call: "a call", sms: "a text", email: "an email", visit: "a visit", payment: "a payment", note: "a note", voicemail: "a voicemail" };
const STATUS_WORD = { open: "Open", in_progress: "Being handled", waiting: "Waiting on them", resolved: "Resolved" };

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export function sourceSentence(issue) {
  const s = issue?.source || {};
  const word = SOURCE_WORD[s.type] || (s.type ? `a ${String(s.type).replace(/_/g, " ")}` : "the record");
  return `From ${word}${s.at ? ` on ${when(s.at)}` : ""}`;
}

export function slaSentence(issue) {
  const sla = issue?.sla || {};
  const days = daysOpen(issue);
  const open = days == null ? "" : days === 0 ? "opened today" : `${days} day${days === 1 ? "" : "s"} open`;
  if (sla.state === "breached") return [open, "past its promise"].filter(Boolean).join(" · ");
  if (sla.state === "due_soon") return [open, sla.due_at ? `due ${when(sla.due_at)}` : "due soon"].filter(Boolean).join(" · ");
  return open;
}

function ActionButton({ issue, onAction, size = "sm" }) {
  const a = issue?.action || {};
  const kind = a.kind || (issue?.customer?.phone ? "call" : "open");
  const icon = kind === "call" ? <Phone size={14} /> : kind === "text" ? <MessageSquare size={14} /> : kind === "task" ? <ClipboardList size={14} /> : <ArrowUpRight size={14} />;
  const label = a.label || (kind === "call" ? "Call" : kind === "text" ? "Text" : kind === "task" ? "Make a task" : "Open");
  return (
    <Button size={size} variant="secondary" icon={icon} onClick={() => onAction?.(kind, issue)}>{label}</Button>
  );
}

export default function IssueCard({ issue, onAction, showCustomer = false, compact = false }) {
  if (!issue) return null;
  const owner = issue.owner?.name || issue.owner_name || "";
  return (
    <article className={`issue${compact ? " issue--compact" : ""}`} data-issue={issue.id}>
      <div className="issue__top">
        {showCustomer && issue.customer?.name ? <span className="issue__who">{issue.customer.name}</span> : null}
        <Badge tone={issueTone(issue)}>{STATUS_WORD[issue.status] || issue.status || "Open"}</Badge>
        <span className="issue__sla">{slaSentence(issue)}</span>
      </div>
      <h4 className="issue__title">{issue.title || issue.issue || issue.summary || "Needs a look"}</h4>
      {issue.quote ? <blockquote className="issue__quote">“{issue.quote}”</blockquote> : null}
      <div className="issue__foot">
        <span className="issue__src">{sourceSentence(issue)}{owner ? ` · ${owner} owns it` : " · nobody owns it yet"}</span>
        <ActionButton issue={issue} onAction={onAction} />
      </div>
    </article>
  );
}
